import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Dropdown, Select } from 'antd';

import OwnAccounts from './OwnAccounts';
import InternalOperations from './InternalOperations';
import InterbankOperations from './InterbankOperations';
import Swift from './Swift';

import { useAppSelector } from '@hooks/redux';
import { useTranslation } from 'react-i18next';
import RubOperations from './RubOperations';
import Exchange from './Exchange';
import Electric from './Electric';

import type { MenuProps } from 'antd';
import Asycuda from './Asycuda';
import BulkTransactionList from '@components/BulkTransaction/BulkTransactionList';
import Disposal from './Disposal';
import BulkTransactionInfo from '@components/BulkTransaction/BulkTransactionInfo';
import SwiftTjs from './SwiftTjs';

function PaymentsIndex() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const paymentsPermissions = useAppSelector(state => state.userReducer.paymentsPermissions);

  const routes: any = [];

  const permissionsRoutes = [
    {
      key: 'own',
      label: t('Own accounts payment shortname'),
      value: '/payments/own',
      element: <OwnAccounts />,
    },
    {
      key: 'internal',
      label: t('Intrabank'),
      value: '/payments/internal',
      element: <InternalOperations />,
    },
    {
      key: 'interbank',
      label: t('Interbank'),
      value: '/payments/interbank',
      element: <InterbankOperations />,
    },
    {
      key: 'asycuda',
      label: t('Custom payments'),
      value: '/payments/asycuda',
      element: <Asycuda />,
    },
    {
      key: 'swift',
      label: t('International'),
      value: '/payments/swift',
      element: <Swift />,
    },

    {
      key: 'swift_rub',
      label: t('Ruble transfer'),
      value: '/payments/swift_rub',
      element: <RubOperations />,
    },
    {
      key: 'exchange',
      label: t('Exchange'),
      value: '/payments/exchange',
      element: <Exchange />,
    },
  ];

  const permissionsRoutesMobile = [
    {
      key: 'own',
      label: t('Between own accounts'),
      value: '/payments/own',
      element: <OwnAccounts />,
    },

    {
      key: 'internal',
      label: t('Intrabank'),
      value: '/payments/internal',
      element: <InternalOperations />,
    },
    {
      key: 'interbank',
      label: t('Interbank'),
      value: '/payments/interbank',
      element: <InterbankOperations />,
    },
    {
      key: 'swift',
      label: t('International'),
      value: '/payments/swift',
      element: <Swift />,
    },
    {
      key: 'swift_rub',
      label: t('Ruble transfer'),
      value: '/payments/swift_rub',
      element: <RubOperations />,
    },
    {
      key: 'exchange',
      label: t('Exchange'),
      value: '/payments/exchange',
      element: <Exchange />,
    },
    {
      key: 'electric',
      label: t('Electric'),
      value: '/payments/electric',
      element: <Electric />,
    },
    {
      key: 'asycuda',
      label: t('Custom payments'),
      value: '/payments/asycuda',
      element: <Asycuda />,
    },
  ];

  const checkActiveRoute = (route: any) => {
    if (location.pathname === route) {
      return 'header__items-active';
    }
    return null;
  };

  const checkRoutes = () => {
    if (window.innerWidth < 768) {
      for (let i = 0; i < permissionsRoutesMobile.length; i++) {
        for (let k = 0; k < paymentsPermissions.length; k++) {
          if (permissionsRoutesMobile[i].key === paymentsPermissions[k]?.operation_short_name) {
            routes.push(permissionsRoutesMobile[i]);
          }
        }
      }
    } else {
      for (let i = 0; i < permissionsRoutes.length; i++) {
        for (let k = 0; k < paymentsPermissions.length; k++) {
          if (permissionsRoutes[i].key === paymentsPermissions[k]?.operation_short_name) {
            routes.push(permissionsRoutes[i]);
          }
        }
      }
    }
  };

  checkRoutes();

  const hasElectric = () => paymentsPermissions.find((item: any) => item.operation_short_name === 'electric');
  const hasBulk = () => paymentsPermissions.find((item: any) => item.operation_short_name === 'bulk');
  const hasSwiftTjs = () => paymentsPermissions.find((item: any) => item.operation_short_name === 'swift_tjs');

  const moreMenuLinkStyle = { fontSize: '14px' };

  const items: MenuProps['items'] = [
    {
      key: 'disposal',
      label: (
        <Link to={'/payments/disposal'} style={moreMenuLinkStyle}>
          {t('Disposal')}
        </Link>
      ),
    },
  ];

  if (hasSwiftTjs()) {
    items.push({
      key: 'swift_tjs',
      label: (
        <Link to={'/payments/swift_tjs'} style={moreMenuLinkStyle}>
          {t('International tjs')}
        </Link>
      ),
    });
  }

  if (hasElectric()) {
    items.push({
      key: 'electric',
      label: (
        <Link to={'/payments/electric'} style={moreMenuLinkStyle}>
          {t('Electric')}
        </Link>
      ),
    });
  }

  if (hasBulk()) {
    items.push({
      key: 'bulk',
      label: (
        <Link to={'/payments/bulk/list'} style={moreMenuLinkStyle}>
          {t('Upload file')}
        </Link>
      ),
    });
  }

  return (
    <div className='flex justify-center'>
      <div className='lg:w-[1130px] w-full max-lg:px-4'>
        <div className='flex'>
          <div className='py-4 lg:py-8 w-full h-full bg-white mb-12'>
            <div className='flex justify-between md:hidden'>
              <h1 className='text-xl font-medium color-active'>{t('Payments')}</h1>
              <Select
                defaultValue='/payments/own'
                style={{ width: 220 }}
                options={routes}
                onChange={(item: any) => navigate(item)}
              />
            </div>

            <div className='flex max-md:hidden' style={{ borderBottom: '1px solid #eee' }}>
              {routes.map((item: any) => (
                <Link
                  key={item.key}
                  to={item.value}
                  className={`header__items py-5 ${checkActiveRoute(item.value)} mr-5 font-medium`}
                  style={{ fontSize: '14px' }}
                >
                  {item.label}
                </Link>
              ))}
              <div className='header__items py-5 null mr-5 relative font-medium text-sm'>
                <Dropdown menu={{ items }}>
                  <span>{t('More')}</span>
                </Dropdown>
                <div className='absolute top-[5px] right-[-17px]'>
                  <p className='text-sm text-red-600'>{t('New')}</p>
                </div>
              </div>
            </div>
            <Routes>
              <Route path={'/disposal'} element={<Disposal />} key={'disposal'} />
              <Route path={'/swift_tjs'} element={<SwiftTjs />} key={'swift_tjs'} />
              {hasElectric() ? <Route path={'/electric'} element={<Electric />} key={'electric'} /> : null}
              {hasBulk() ? <Route path={'/bulk/list'} element={<BulkTransactionList />} key={'bulk'} /> : null}
              {hasBulk() ? <Route path={'/bulk/info/*'} element={<BulkTransactionInfo />} key={'bulk'} /> : null}
              {routes.map((item: any) => (
                <Route path={`${item.key}/*`} element={item.element} key={item.key} />
              ))}

              <Route path='*' element={<Navigate to='/' replace />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentsIndex;
